import React, { useEffect, useState } from "react";
import { Download, Mail, MessageCircle, Copy, QrCode, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const inputCls = "bg-zinc-950 border-zinc-800";
const saveBlob = (data, name) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const CHANNEL = { email: "Email", whatsapp: "WhatsApp", link: "Link" };

// Elenco invii con aperture (chi, quando, da quale dispositivo)
function SharesList({ shares, loading, onRefresh }) {
  return (
    <div className="rounded-xl border border-zinc-800 p-3 space-y-2" data-testid="brochure-shares">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-amber-300" /> Invii e aperture ({shares.length})</div>
        <button type="button" onClick={onRefresh} className="text-[11px] text-zinc-500 hover:text-zinc-200 flex items-center gap-1" data-testid="brochure-shares-refresh"><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> aggiorna</button>
      </div>
      {shares.length === 0 && <p className="text-[11px] text-zinc-500">Nessun invio ancora: ogni link inviato via email, WhatsApp o copiato viene tracciato qui con le aperture.</p>}
      <div className="max-h-56 overflow-auto divide-y divide-zinc-800/70">
        {shares.map((s) => (
          <div key={s.id} className="py-1.5 flex items-start gap-3 text-[11px]" data-testid={`brochure-share-${s.id}`}>
            <div className="w-24 shrink-0 text-zinc-500">{fmt(s.created_at)}<div className="text-zinc-400">{CHANNEL[s.channel] || s.channel} · {s.club_name}</div></div>
            <div className="flex-1 min-w-0">
              <div className="text-zinc-200 truncate">{s.recipient_name || s.to_email || "destinatario non indicato"}{s.recipient_name && s.to_email ? ` · ${s.to_email}` : ""}</div>
              <div className="text-zinc-500 truncate">inviata da {s.admin_email}{s.devices?.length ? ` · ${s.devices.join(", ")}` : ""}</div>
            </div>
            <div className={`shrink-0 text-right ${s.open_count ? "text-emerald-300" : "text-zinc-500"}`} data-testid={`brochure-share-opens-${s.id}`}>
              <div className="font-semibold">{s.open_count ? `${s.open_count} apertur${s.open_count === 1 ? "a" : "e"}` : "non aperta"}</div>
              {s.last_open && <div className="text-zinc-500">ultima {fmt(s.last_open)}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Presentazione PDF (piattaforma o brandizzata per club): download, email, WhatsApp, link, locandina QR, tracciamento
export function BrochureDialog({ tenants = [], initialClub = "" }) {
  const [club, setClub] = useState(initialClub);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const tenant = tenants.find((t) => t.slug === club);
  const label = tenant ? tenant.name : "Nouvelle Fidelity Platform";
  const loadShares = () => { setLoadingShares(true); api.get("/platform/brochure/shares").then((r) => setShares(r.data || [])).catch(() => {}).finally(() => setLoadingShares(false)); };
  useEffect(() => { loadShares(); }, []);

  const download = async () => {
    setBusy(true);
    try { const { data } = await api.get("/platform/brochure.pdf", { params: club ? { club } : {}, responseType: "blob" }); saveBlob(data, `presentazione_${club || "nouvelle_fidelity"}.pdf`); toast.success("Presentazione PDF scaricata"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const poster = async () => {
    if (!tenant) return;
    setBusy(true);
    try { const { data } = await api.get(`/platform/tenants/${tenant.id}/qr-poster.pdf`, { responseType: "blob" }); saveBlob(data, `locandina_qr_${club}.pdf`); toast.success("Locandina QR scaricata (A4 + 4 cartellini)"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const share = async (channel) => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/brochure/share", { club: club || null, channel, to_email: channel === "email" ? email : null, recipient_name: name || null, message: message || null });
      if (channel === "email") toast.success(data.email?.attachment ? `Email con PDF inviata a ${email}` : `Email con link inviata a ${email} (SMTP non configurato: senza allegato)`);
      loadShares();
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; } finally { setBusy(false); }
  };
  const whatsapp = async () => { const d = await share("whatsapp"); if (d) window.open(`https://wa.me/?text=${encodeURIComponent(d.whatsapp_text)}`, "_blank"); };
  const copyLink = async () => { const d = await share("link"); if (d) { await navigator.clipboard.writeText(d.link); toast.success("Link tracciato copiato"); } };

  return (
    <div className="space-y-4" data-testid="brochure-dialog">
      <div>
        <Label className="text-xs text-zinc-400">Versione</Label>
        <select value={club} onChange={(e) => setClub(e.target.value)} data-testid="brochure-club" className={`${inputCls} w-full h-10 rounded-md border px-3 text-sm`}>
          <option value="">Piattaforma Nouvelle Fidelity (generica, per nuovi circuiti)</option>
          {tenants.map((t) => <option key={t.slug} value={t.slug}>{t.name} · brandizzata con logo e colori ({t.slug})</option>)}
        </select>
        <p className="text-[11px] text-zinc-500 mt-1">{tenant ? `Versione per i negozi del circuito ${tenant.name}: logo, colori, link e QR del club.` : "Versione commerciale per presentare la piattaforma a nuovi circuiti."}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={download} disabled={busy} data-testid="brochure-download" className="bg-amber-500 hover:bg-amber-400 text-zinc-950"><Download className="h-4 w-4 mr-1" /> Scarica PDF</Button>
        <Button variant="outline" onClick={whatsapp} disabled={busy} data-testid="brochure-whatsapp" className="border-zinc-800"><MessageCircle className="h-4 w-4 mr-1 text-emerald-400" /> Invia via WhatsApp</Button>
        <Button variant="outline" onClick={copyLink} disabled={busy} data-testid="brochure-copy-link" className="border-zinc-800"><Copy className="h-4 w-4 mr-1" /> Copia link</Button>
        {tenant && <Button variant="outline" onClick={poster} disabled={busy} data-testid="brochure-qr-poster" className="border-zinc-800"><QrCode className="h-4 w-4 mr-1 text-sky-400" /> Locandina QR</Button>}
      </div>
      <div className="rounded-xl border border-zinc-800 p-3 space-y-2">
        <div className="text-xs font-semibold flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-sky-400" /> Destinatario (per email, WhatsApp e tracciamento)</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <div><Label className="text-xs text-zinc-400">Email destinatario</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" className={inputCls} data-testid="brochure-email" /></div>
          <div><Label className="text-xs text-zinc-400">Nome (saluto)</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario" className={inputCls} data-testid="brochure-name" /></div>
        </div>
        <div><Label className="text-xs text-zinc-400">Messaggio personale (facoltativo)</Label><Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Come anticipato al telefono…" className={inputCls} data-testid="brochure-message" /></div>
        <Button onClick={() => share("email")} disabled={busy || !/^\S+@\S+\.\S+$/.test(email)} data-testid="brochure-send-email" className="bg-sky-600 hover:bg-sky-500 text-white"><Mail className="h-4 w-4 mr-1" /> Invia presentazione {label} via email</Button>
      </div>
      <SharesList shares={shares} loading={loadingShares} onRefresh={loadShares} />
    </div>
  );
}
